<script setup lang="ts">
// Operations' bottom ledger: the workbench's four collections as dense tables
// over the map, one tab at a time — the shared workbench drawer with the left
// panel carrying the selected record instead of this drawer repeating it.
//
// Every row is built here from the page's collections rather than read from a
// store, so the drawer states no state of its own: the page owns the active tab
// and the selection, and this only hands rows to `WorkbenchLedger`. The badge on
// each tab is the length of the list under it — one collection, so the count and
// the rows can never disagree.
import { computed } from "vue";

import { INSTANCE_STATE_LABELS } from "@/components/map-review/columns";
import { formatDate, formatDuration } from "@/format";
import WorkbenchLedger from "@/components/ledger/WorkbenchLedger.vue";
import type {
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	OperationsWorkRow,
	RoutePlan,
	SyncAssignment,
	SyncRoute,
	User,
} from "@/type/api";

import type { LedgerColumn, LedgerRow, LedgerTab } from "./ledger";
import {
	TRAP_COLLECTED_COLOR,
	TRAP_OPEN_COLOR,
	routeColor,
	type Selection,
} from "./model";
import {
	assignmentWindowText,
	isOpenAssignment,
	kindLabel,
	routeDay,
	stateLabel,
	targetLabel,
	technicianLabel,
} from "./wire";

const props = defineProps<{
	assignments: SyncAssignment[];
	routes: SyncRoute[];
	/** The day's drive plans, keyed by route id. A Route the day board passed
	 * over has no entry, which is what the Schedule tab prints as "—". */
	plans: Map<number, RoutePlan>;
	/** The day the Schedule tab reads, as the page's day selector states it. */
	dayWindow: { start: string; end: string };
	work: OperationsWorkRow[];
	selectedKeys: Set<string>;
	traps: MapReviewTrapInstanceRow[];
	users: User[];
	sites: MapReviewSiteRow[];
	selection: Selection | null;
	fraction: number;
	expanded: boolean;
	/** The tab the page has open, and the drawn area narrowing it. */
	activeKey: string;
	areaLabel?: string | null;
	areaKeys?: Set<string> | null;
}>();

const emit = defineEmits<{
	select: [row: LedgerRow, additive: boolean];
	toggle: [row: LedgerRow];
	"update:activeKey": [key: string];
	"update:fraction": [fraction: number];
	"update:expanded": [expanded: boolean];
	"clear-area": [];
}>();

const ASSIGNMENT_COLUMNS: LedgerColumn[] = [
	{ key: "id", label: "#", align: "end", width: "4.5rem" },
	{ key: "kind", label: "Kind", width: "7.5rem" },
	{ key: "state", label: "State", width: "7rem" },
	{ key: "technician", label: "Technician" },
	{ key: "target", label: "Target" },
	{ key: "window", label: "Window", width: "14rem" },
	{ key: "route", label: "Route", align: "end", width: "4.5rem" },
];

const ROUTE_COLUMNS: LedgerColumn[] = [
	{ key: "id", label: "#", align: "end", width: "4.5rem" },
	{ key: "technician", label: "Technician" },
	{ key: "state", label: "State", width: "7rem" },
	{ key: "window", label: "Window start", width: "10rem" },
	{ key: "members", label: "Members", align: "end", width: "6rem" },
	{ key: "unsequenced", label: "Unsequenced", align: "end", width: "7rem" },
];

/** The Schedule tab reads a Route through its plan. Every column past the
 * technician is something the plan states, and the last is the plan's own
 * verdict on the day — so the columns are the day's questions in the order an
 * office asks them. */
const SCHEDULE_COLUMNS: LedgerColumn[] = [
	{ key: "id", label: "#", align: "end", width: "4.5rem" },
	{ key: "technician", label: "Technician" },
	{ key: "window", label: "Window", width: "14rem" },
	{ key: "stops", label: "Stops", align: "end", width: "5rem" },
	{ key: "drive", label: "Drive", align: "end", width: "6rem" },
	{ key: "service", label: "Service", align: "end", width: "6rem" },
	{ key: "finish", label: "Finish", align: "end", width: "6rem" },
	{ key: "conflicts", label: "Conflicts", width: "10rem" },
];

const TRAP_COLUMNS: LedgerColumn[] = [
	{ key: "id", label: "#", align: "end", width: "4.5rem" },
	{ key: "state", label: "State", width: "6rem" },
	{ key: "site", label: "Site" },
	{ key: "station", label: "Station" },
	{ key: "set", label: "Set", width: "10rem" },
	{ key: "collected", label: "Collected", width: "10rem" },
];

const WORK_COLUMNS: LedgerColumn[] = [
	{ key: "origin", label: "Origin", width: "7rem" },
	{ key: "id", label: "#", align: "end", width: "4rem" },
	{ key: "title", label: "Work" },
	{ key: "state", label: "State", width: "6rem" },
	{ key: "priority", label: "Priority", width: "6rem" },
	{ key: "due", label: "Due", width: "7rem" },
	{ key: "target", label: "Target", width: "9rem" },
];

const TECHNICIAN_COLUMNS: LedgerColumn[] = [
	{ key: "name", label: "Name" },
	{ key: "live", label: "Live work", align: "end", width: "6rem" },
	{ key: "routes", label: "Routes", align: "end", width: "6rem" },
];

/** The work still to do, earliest stated window first. A settled row is history
 * and belongs to the technician's record, not to the queue an office dispatches
 * from. The order the table opens in comes from `defaultSort`; this list is the
 * stable base it falls back to. */
const assignmentRows = computed<LedgerRow[]>(() =>
	props.assignments
		.filter((row) => isOpenAssignment(row))
		.map((row) => {
			const kind = kindLabel(row.kind);
			const state = stateLabel(row.state);
			const technician = technicianLabel(row.technicianId, props.users);
			const target = targetLabel(row, props.sites);
			return {
				kind: "assignment",
				id: row.id,
				cells: [
					`#${row.id}`,
					kind,
					state,
					technician,
					target,
					assignmentWindowText(row),
					row.routeId === null ? "—" : `#${row.routeId}`,
				],
				// The window sorts by its stated start, not by its printed
				// text: an unstated start is a null, which the table keeps last.
				sort: [
					row.id,
					kind,
					state,
					technician,
					target,
					row.startAt === null ? null : new Date(row.startAt).getTime(),
					row.routeId,
				],
			};
		}),
);

const routeRows = computed<LedgerRow[]>(() =>
	props.routes.map((route) => {
		const technician = technicianLabel(route.technicianId, props.users);
		const unsequenced = route.members.filter(
			(member) => member.position === null,
		).length;
		return {
			kind: "route",
			id: route.id,
			// The line the map draws this route in, so the row and the line are
			// read as the same route.
			swatch: routeColor(route.id),
			cells: [
				`#${route.id}`,
				technician,
				route.state,
				routeDay(route),
				String(route.members.length),
				String(unsequenced),
			],
			sort: [
				route.id,
				technician,
				route.state,
				new Date(route.windowStart).getTime(),
				route.members.length,
				unsequenced,
			],
		};
	}),
);

/** The clock time inside a day, without the day repeated: the Schedule states the
 * day once and the times beside it, which is what fits a dense table. */
const scheduleClock = new Intl.DateTimeFormat(undefined, {
	hour: "2-digit",
	minute: "2-digit",
	hour12: false,
});

/** A Route's window as the Schedule states it: the day it opens, then the local
 * range. A window closing on a later day states that instant in full, so a
 * midnight end never reads as the same afternoon. */
function scheduleWindowText(route: SyncRoute): string {
	const start = new Date(route.windowStart);
	const end = new Date(route.windowEnd);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return "—";
	}
	const from = scheduleClock.format(start);
	return start.toDateString() === end.toDateString()
		? `${routeDay(route)}, ${from} → ${scheduleClock.format(end)}`
		: `${routeDay(route)}, ${from} → ${formatDate(end)}`;
}

/** When the plan finishes, read in the day the Route opens: a plan running past
 * midnight states the instant rather than a clock time that would read as
 * earlier than its start. */
function scheduleFinishText(
	route: SyncRoute,
	plan: RoutePlan | undefined,
): string {
	if (plan === undefined || plan.finishesAt === null) {
		return "—";
	}
	const finish = new Date(plan.finishesAt);
	if (Number.isNaN(finish.getTime())) {
		return "—";
	}
	return new Date(route.windowStart).toDateString() === finish.toDateString()
		? scheduleClock.format(finish)
		: formatDate(finish);
}

/** How the plan's stops landed, counted by why. `late` is a window the order
 * could not keep; `unroutable` is the engine failing to place a point;
 * `no_point` is the Assignment naming no location at all. They are counted
 * apart because the three call for different work, and one conflated number
 * would hide which. */
interface PlanConflicts {
	late: number;
	unroutable: number;
	noPoint: number;
}

function planConflicts(plan: RoutePlan | undefined): PlanConflicts | null {
	if (plan === undefined) return null;
	const conflicts: PlanConflicts = { late: 0, unroutable: 0, noPoint: 0 };
	for (const stop of plan.stops) {
		switch (stop.arrivalState) {
			case "late":
				conflicts.late += 1;
				break;
			case "unroutable":
				conflicts.unroutable += 1;
				break;
			case "no_point":
				conflicts.noPoint += 1;
				break;
		}
	}
	return conflicts;
}

function conflictText(conflicts: PlanConflicts | null): string {
	if (conflicts === null) return "—";
	const parts: string[] = [];
	if (conflicts.late > 0) parts.push(`${conflicts.late} late`);
	if (conflicts.unroutable > 0)
		parts.push(`${conflicts.unroutable} unroutable`);
	if (conflicts.noPoint > 0) parts.push(`${conflicts.noPoint} no point`);
	return parts.length === 0 ? "—" : parts.join(" · ");
}

/** The day's work as one time-ordered reading of Routes: a row per Route the
 * selected day touches, earliest first. Nothing new is named — the row *is* the
 * Route the Routes tab lists, so its kind, id and swatch are the same, and
 * selecting it selects that Route on the map.
 *
 * The bounds arrive in the day selector's own form (a bare local
 * `YYYY-MM-DDTHH:mm`, `defaultWindow()` in `wire.ts`), which `Date` reads as
 * local time; they choose the rows and are never printed. A Route with no plan
 * keeps its row and prints "—" where the plan would speak: the route is still
 * dispatched, and hiding it would report a planning gap as an absence of work. */
const scheduleRows = computed<LedgerRow[]>(() => {
	const dayStart = new Date(props.dayWindow.start).getTime();
	const dayEnd = new Date(props.dayWindow.end).getTime();
	return props.routes
		.filter((route) => {
			const start = new Date(route.windowStart).getTime();
			const end = new Date(route.windowEnd).getTime();
			return start < dayEnd && end > dayStart;
		})
		.sort(
			(left, right) =>
				new Date(left.windowStart).getTime() -
				new Date(right.windowStart).getTime(),
		)
		.map((route) => {
			const plan = props.plans.get(route.id);
			const technician = technicianLabel(route.technicianId, props.users);
			const conflicts = planConflicts(plan);
			const stops =
				plan === undefined ? route.members.length : plan.stops.length;
			return {
				kind: "route",
				id: route.id,
				swatch: routeColor(route.id),
				cells: [
					`#${route.id}`,
					technician,
					scheduleWindowText(route),
					String(stops),
					formatDuration(plan?.travelSeconds ?? null),
					formatDuration(plan?.serviceSeconds ?? null),
					scheduleFinishText(route, plan),
					conflictText(conflicts),
				],
				sort: [
					route.id,
					technician,
					new Date(route.windowStart).getTime(),
					stops,
					plan?.travelSeconds ?? null,
					plan?.serviceSeconds ?? null,
					plan === undefined || plan.finishesAt === null
						? null
						: new Date(plan.finishesAt).getTime(),
					conflicts === null
						? null
						: conflicts.late + conflicts.unroutable + conflicts.noPoint,
				],
			};
		});
});

/** Awaiting longest first: the trap that has been out the longest is the visit
 * the operator should see before the one set this morning. */
const openTraps = computed(() =>
	props.traps
		.filter((trap) => trap.collected_at === null)
		.sort(
			(left, right) =>
				new Date(left.date).getTime() - new Date(right.date).getTime(),
		),
);

/** The collected reading is newest first — enough context to find the trap a
 * conversation is about, not the whole history. */
const collectedTraps = computed(() =>
	props.traps
		.filter((trap) => trap.collected_at !== null)
		.sort(
			(left, right) =>
				new Date(right.date).getTime() - new Date(left.date).getTime(),
		),
);

/** Open instances, then collected ones. The state key in `sort` is what holds
 * that grouping when the tab's own default sort runs over it: 0 for open, 1 for
 * collected, and the table's stable sort keeps each group in the order built
 * here. */
const trapRows = computed<LedgerRow[]>(() =>
	[...openTraps.value, ...collectedTraps.value].map((trap) => {
		const site = trap.site || `Site #${trap.site_id}`;
		const station = trap.station || "off-station";
		return {
			kind: "trap",
			id: trap.id,
			// The map's two readings: orange while a collection is waiting,
			// grey once there is nothing deployed to collect.
			swatch:
				trap.collected_at === null ? TRAP_OPEN_COLOR : TRAP_COLLECTED_COLOR,
			cells: [
				`#${trap.id}`,
				INSTANCE_STATE_LABELS[trap.state] ?? trap.state,
				site,
				station,
				formatDate(new Date(trap.date)),
				trap.collected_at ? formatDate(new Date(trap.collected_at)) : "—",
			],
			sort: [
				trap.id,
				trap.state === "set" ? 0 : 1,
				site,
				station,
				new Date(trap.date).getTime(),
				trap.collected_at === null
					? null
					: new Date(trap.collected_at).getTime(),
			],
		};
	}),
);

/** A state the machine can still move out of is live work; a settled row is
 * history and does not count against the technician. */
const LIVE_STATES: Record<string, true> = {
	draft: true,
	dispatched: true,
	in_progress: true,
	deferred: true,
};

function liveCount(technicianId: number): number {
	return props.assignments.filter(
		(row) =>
			row.technicianId === technicianId && LIVE_STATES[row.state] === true,
	).length;
}

function routeCount(technicianId: number): number {
	return props.routes.filter((route) => route.technicianId === technicianId)
		.length;
}

/** Active technicians only. A technician who has left the district is not
 * dispatchable, so listing them would offer work that can never be carried
 * out; their assignments and routes remain in the collections the other tabs
 * read. */
const technicianRows = computed<LedgerRow[]>(() =>
	props.users
		.filter((user) => user.is_active)
		.sort((left, right) => left.display_name.localeCompare(right.display_name))
		.map((user) => {
			const live = liveCount(user.id);
			const routes = routeCount(user.id);
			return {
				kind: "technician",
				id: user.id,
				cells: [user.display_name, String(live), String(routes)],
				sort: [user.display_name, live, routes],
			};
		}),
);

const workRows = computed<LedgerRow[]>(() =>
	props.work.map((row) => ({
		kind:
			row.origin === "lead"
				? "work_lead"
				: row.origin === "work_order"
					? "work_order"
					: "work_direct",
		id: row.id,
		cells: [
			row.origin,
			`#`,
			row.title,
			row.state,
			row.priority ?? "—",
			row.due_at ? formatDate(new Date(row.due_at)) : "—",
			row.target_summary,
		],
		sort: [
			row.origin,
			row.id,
			row.title,
			row.state,
			row.priority ?? null,
			row.due_at ? new Date(row.due_at).getTime() : null,
			row.target_summary,
		],
	})),
);

const tabs = computed<LedgerTab[]>(() => [
	{
		key: "work",
		label: "Work",
		badgeCount: workRows.value.length,
		columns: WORK_COLUMNS,
		rows: workRows.value,
		defaultSort: { key: "id", descending: false },
	},
	{
		key: "assignments",
		label: "Assignments",
		badgeCount: assignmentRows.value.length,
		columns: ASSIGNMENT_COLUMNS,
		rows: assignmentRows.value,
		defaultSort: { key: "window", descending: false },
	},
	{
		key: "routes",
		label: "Routes",
		badgeCount: routeRows.value.length,
		columns: ROUTE_COLUMNS,
		rows: routeRows.value,
		defaultSort: { key: "window", descending: false },
	},
	{
		key: "schedule",
		label: "Schedule",
		badgeCount: scheduleRows.value.length,
		columns: SCHEDULE_COLUMNS,
		rows: scheduleRows.value,
		defaultSort: { key: "window", descending: false },
	},
	{
		key: "traps",
		label: "Traps",
		badgeCount: openTraps.value.length,
		columns: TRAP_COLUMNS,
		rows: trapRows.value,
		defaultSort: { key: "state", descending: false },
	},
	{
		key: "technicians",
		label: "Technicians",
		badgeCount: technicianRows.value.length,
		columns: TECHNICIAN_COLUMNS,
		rows: technicianRows.value,
		defaultSort: { key: "name", descending: false },
	},
]);
</script>

<template>
	<WorkbenchLedger
		class="operations-ledger"
		:tabs="tabs"
		:active-key="activeKey"
		:fraction="fraction"
		:expanded="expanded"
		:selection="selection"
		:multi="true"
		:selected-keys="selectedKeys"
		:area-label="areaLabel"
		:area-keys="areaKeys"
		@update:active-key="emit('update:activeKey', $event)"
		@select="(row, additive) => emit('select', row, additive)"
		@toggle="(row) => emit('toggle', row)"
		@update:fraction="emit('update:fraction', $event)"
		@update:expanded="emit('update:expanded', $event)"
		@clear-area="emit('clear-area')"
	>
	</WorkbenchLedger>
</template>

<style scoped>
/* Placement only: the page's map is the positioned box this drawer sits in. The
   shared widget owns the bar, the tabs, the table and the drawer's own stacking
   (z-index 20, under the raised rail's 25), so nothing here restates them. */
.operations-ledger {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
}
</style>
